import { computed, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { environment } from '../environments/environment';
import {
  EtichettaMessaggioDTO,
  EtichettaMessaggioRTO,
  SetEtichettaMessaggioDTO,
} from '../models/etichetta-messaggio.model';

@Injectable({
  providedIn: 'root',
})
export class EtichetteMessaggioService {
  apiVersion = 'v1';
  private apiUrl = `${environment.apiUrl}/api/${this.apiVersion}`;
  private ETICHETTE_MESSAGGI = "/etichette-messaggio";
  private ARCHIVIO_FILTER = "/archivio";

  /** Etichetta PEC_WGo: esistono nel DB ma non devono comparire in elenchi e filtri. */
  private static readonly ETICHETTE_NASCOSTE = new Set(['PEC_WG']);

  private allEtichette = signal<EtichettaMessaggioRTO[]>([]);

  etichette = computed(() =>
    this.allEtichette().filter(
      e => !EtichetteMessaggioService.ETICHETTE_NASCOSTE.has(e.nomeEtichetta),
    ),
  );

  /**
   * Etichetta selezionata nelle sotto-voci di sidebar. `null` = nessun filtro.
   * È la sorgente di verità per il filtro delle liste, come `casellaMonitorata` lo è
   * per la casella: le pagine reagiscono al suo cambiamento ricaricando la prima pagina.
   */
  readonly etichettaSelezionata = signal<EtichettaMessaggioRTO | null>(null);

  /** Seleziona l'etichetta, oppure azzera il filtro se era già quella attiva. */
  toggleEtichettaSelezionata(etichetta: EtichettaMessaggioRTO) {
    this.etichettaSelezionata.update(current =>
      current?.id === etichetta.id ? null : etichetta,
    );
  }

  constructor(private http: HttpClient) {}

  get(urlPath: string) {
    return this.http.get<EtichettaMessaggioRTO[]>(this.apiUrl + urlPath);
  }

  fetchEtichetteArchived() {
    this.get(this.ETICHETTE_MESSAGGI + this.ARCHIVIO_FILTER).subscribe({
      next: etichette => this.allEtichette.set(etichette ?? []),
      error: err => console.error('[EtichetteMessaggioService] errore recupero etichette:', err),
    });
  }

  fetchAll() {
    this.get(this.ETICHETTE_MESSAGGI).subscribe({
      next: etichette => this.allEtichette.set(etichette ?? []),
      error: err => console.error('[EtichetteMessaggioService] errore recupero etichette:', err),
    });
  }

  create(dto: EtichettaMessaggioDTO) {
    return this.http.post<EtichettaMessaggioRTO>(this.apiUrl + this.ETICHETTE_MESSAGGI, dto);
  }

  /** Associa una singola etichetta a un singolo messaggio. */
  setEtichetta(dto: SetEtichettaMessaggioDTO) {
    return this.http.post<void>(`${this.apiUrl}${this.ETICHETTE_MESSAGGI}/set`, dto);
  }

  /**
   * L'endpoint `/set` lavora su un messaggio alla volta: per l'azione massiva della toolbar
   * inoltriamo una richiesta per messaggio e attendiamo il completamento di tutte.
   */
  setEtichettaMessaggi(idMessaggi: number[], idEtichetta: number): Observable<void[]> {
    if (idMessaggi.length === 0) return of([]);
    return forkJoin(idMessaggi.map(idMessaggio => this.setEtichetta({ idMessaggio, idEtichetta })));
  }

  /**
   * Inserisce l'etichetta appena creata nello store condiviso, così sidebar e dialog
   * si aggiornano senza dover rifare una GET completa.
   */
  addToStore(etichetta: EtichettaMessaggioRTO) {
    this.allEtichette.update(list =>
      list.some(e => e.id === etichetta.id) ? list : [...list, etichetta],
    );
  }
}
